import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MaintenancesApi } from '@api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Equipment, EquipmentMaintenanceMapped } from '@core/interfaces';
import { EquipmentsApi } from 'app/main/equipments/services/api/equipments.api';
import { DialogEditMaintenanceComponent } from 'app/main/maintenances/dialogs/dialog-edit-maintenance/dialog-maintenances.component';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EquipmentMaintenanceMapperService } from './services/tab-maintenances.service';
import { UpdatedByService } from './services/updated-by.service';


@Component({
  selector: 'tab-maintenances',
  templateUrl: './tab-maintenances.component.html',
  styleUrls: ['./tab-maintenances.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TabMaintenancesComponent implements OnInit, OnDestroy {

  public isOpeningEquipmentMaintenanceDialog: Boolean = false;
  public equipmentMaintenancesMapped: EquipmentMaintenanceMapped[] = [];
  public hasEditMaintenancePermission = this.authenticationService.hasPermission('maintenances-edit');
  public equipment: Equipment;
  public isLoadedAllTheMaintenances: Boolean = false;
  public loading = false;
  public offset = 0;
  public limit = 25;

  private hasMoreMaintenances = true;

  private readonly subscriptions = new Subject<void>();

  @ViewChild('scrollableContent', { static: true })
  public scrollableContent: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private equipmentMaintenanceMapper: EquipmentMaintenanceMapperService,
    private authenticationService: AuthenticationService,
    private maintenancesApi: MaintenancesApi,
    private updatedService: UpdatedByService,
    private matDialog: MatDialog,
    private equipmentApi: EquipmentsApi
  ) {}

  async ngOnInit(): Promise<void> {
    this.equipment = this.dialogData.equipment;

    fromEvent(this.scrollableContent.nativeElement, 'scroll').pipe(
      takeUntil(this.subscriptions),
      filter(() => this.isOnEndOfThePage() && this.hasMoreMaintenances),
      tap(() => this.loading = true),
      debounceTime(500),
      switchMap(() => this.loadMoreMaintenances())
    ).subscribe();

    await this.loadMoreMaintenances();
  }

  async ngOnDestroy(): Promise<void> {
    this.subscriptions.next();
    this.subscriptions.complete();
  }


  public async loadMoreMaintenances(): Promise<void> {
    this.loading = true;

    const maintenances = await this.equipmentApi.findEquipmentMaintenances(this.equipment.id, this.offset, this.limit)
      .then((result) => this.equipmentMaintenanceMapper.toEquipmentMaintenancesMapped(result))
      .then((result) => this.updatedService.customUpdatedByIn(result));

    this.equipmentMaintenancesMapped.push(...maintenances);

    this.offset = this.equipmentMaintenancesMapped.length;
    this.hasMoreMaintenances = maintenances.length === this.limit;
    this.loading = false;
  }

  public openEquipmentMaintenanceDialog(maintenanceId: string): void {
    this.isOpeningEquipmentMaintenanceDialog = true;

    this.maintenancesApi.searchOne(maintenanceId).subscribe((maintenance) => {
      const maintenanceDialog = this.matDialog.open(DialogEditMaintenanceComponent, {
        panelClass: 'dialog-edit-maintenance',
        data: {
          maintenance,
          isViewMode: !this.hasEditMaintenancePermission
        }
      });

      maintenanceDialog.afterClosed().subscribe(() => {
        this.isOpeningEquipmentMaintenanceDialog = false;
      });
    });
  }

  isOnEndOfThePage(): boolean {
    const { scrollHeight, clientHeight, scrollTop } = this.scrollableContent.nativeElement;
    return Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
  }
}
